import React, { useState, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import apiClient from "../../../api/apiClient";
import { API_BASE_URL } from "../../../api/header";
import { useMutation } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  User,
  KeyRound,
  Lock,
  Eye,
  EyeOff,
  Loader2,
  ArrowLeft,
  MailCheck,
  ShieldCheck,
} from "lucide-react";
import { toast } from "sonner";
import { all_routes } from "../../../Router/all_routes";
import { cn } from "@/lib/utils";

// ── Step indicator ────────────────────────────────────────────────────────────
const StepIndicator = ({ current }) => {
  const steps = [
    { n: 1, label: "Username" },
    { n: 2, label: "Verify OTP" },
    { n: 3, label: "New Password" },
  ];
  return (
    <div className="flex items-center justify-center gap-0 mb-6">
      {steps.map((s, idx) => (
        <React.Fragment key={s.n}>
          <div className="flex flex-col items-center gap-1">
            <div
              className={cn(
                "h-8 w-8 rounded-full flex items-center justify-center text-xs font-bold border-2 transition-all",
                current > s.n
                  ? "bg-primary border-primary text-primary-foreground"
                  : current === s.n
                    ? "bg-primary/10 border-primary text-primary"
                    : "bg-muted border-border text-muted-foreground",
              )}
            >
              {current > s.n ? "✓" : s.n}
            </div>
            <span
              className={cn(
                "text-[10px] font-semibold tracking-wide",
                current === s.n ? "text-primary" : "text-muted-foreground",
              )}
            >
              {s.label}
            </span>
          </div>
          {idx < steps.length - 1 && (
            <div
              className={cn(
                "h-0.5 w-10 mx-1 mb-5 transition-all",
                current > s.n + 1
                  ? "bg-primary"
                  : current > s.n
                    ? "bg-primary"
                    : "bg-border",
              )}
            />
          )}
        </React.Fragment>
      ))}
    </div>
  );
};

// ── OTP input (6 individual boxes) ───────────────────────────────────────────
const OtpInput = ({ value, onChange }) => {
  const inputRefs = useRef([]);

  const handleChange = (idx, e) => {
    // If user adds more than one character (e.g. paste), just take the last digit
    const char = e.target.value.replace(/\D/g, "").slice(-1);
    const arr = value.split("");
    arr[idx] = char || " ";
    const next = arr.join("").padEnd(6, " ").slice(0, 6);
    onChange(next);
    if (char && idx < 5) {
      inputRefs.current[idx + 1]?.focus();
    }
  };

  const handleKeyDown = (idx, e) => {
    if (e.key === "Backspace") {
      const arr = value.split("");
      if (arr[idx] !== " ") {
        arr[idx] = " ";
        onChange(arr.join("").padEnd(6, " ").slice(0, 6));
      } else if (idx > 0) {
        inputRefs.current[idx - 1]?.focus();
        const arr2 = value.split("");
        arr2[idx - 1] = " ";
        onChange(arr2.join("").padEnd(6, " ").slice(0, 6));
      }
    } else if (e.key === "ArrowLeft" && idx > 0) {
      inputRefs.current[idx - 1]?.focus();
    } else if (e.key === "ArrowRight" && idx < 5) {
      inputRefs.current[idx + 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData
      .getData("text")
      .replace(/\D/g, "")
      .slice(0, 6);
    onChange(pasted.padEnd(6, " ").slice(0, 6));
    const lastIdx = Math.min(pasted.length, 5);
    inputRefs.current[lastIdx]?.focus();
  };

  return (
    <div className="flex gap-2 justify-center">
      {Array.from({ length: 6 }).map((_, idx) => (
        <input
          key={idx}
          ref={(el) => (inputRefs.current[idx] = el)}
          type="text"
          inputMode="numeric"
          maxLength={2} // Allow typing to be caught even if there's already a char
          value={value[idx] !== " " ? value[idx] || "" : ""}
          onChange={(e) => handleChange(idx, e)}
          onKeyDown={(e) => handleKeyDown(idx, e)}
          onPaste={handlePaste}
          className={cn(
            "h-12 w-10 rounded-xl border-2 bg-background/50 text-center text-xl font-bold transition-all outline-none",
            value[idx] && value[idx] !== " "
              ? "border-primary text-foreground"
              : "border-border text-muted-foreground",
            "focus:border-primary focus:ring-2 focus:ring-primary/20",
          )}
        />
      ))}
    </div>
  );
};

// ── Main component ────────────────────────────────────────────────────────────
const ForgotPassword = () => {
  const navigate = useNavigate();
  const route = all_routes;

  const [step, setStep] = useState(1);
  const [username, setUsername] = useState("");
  const [otp, setOtp] = useState("      "); // 6 spaces as placeholder
  const [resetToken, setResetToken] = useState("");
  const [password, setPassword] = useState("");
  const [passwordConfirm, setPasswordConfirm] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  // Normalise OTP value so it's always 6-chars padded with spaces
  const otpValue = otp.padEnd(6, " ").slice(0, 6);
  const otpDigits = otpValue.replace(/\s/g, "");

  // ── Step 1: send OTP ────────────────────────────────────────────────────────
  const sendOtpMutation = useMutation({
    mutationFn: async () => {
      const res = await apiClient.post(`/forgot-password`, {
        username: username.trim(),
      });
      return res.data;
    },
    onSuccess: () => {
      toast.success("OTP sent - check the email linked to this account.");
      setStep(2);
    },
    onError: (err) => {
      // API always returns 200, but handle network errors gracefully
      const msg =
        err.response?.data?.message ||
        err.message ||
        "Something went wrong. Please try again.";
      toast.error(msg);
    },
  });

  // ── Step 2: verify OTP ──────────────────────────────────────────────────────
  const verifyOtpMutation = useMutation({
    mutationFn: async () => {
      const res = await apiClient.post(`/forgot-password/verify`, {
        username: username.trim(),
        otp: otpDigits,
      });
      return res.data;
    },
    onSuccess: (data) => {
      setResetToken(data.reset_token);
      toast.success("OTP verified!");
      setStep(3);
    },
    onError: (err) => {
      const msg =
        err.response?.data?.message || err.message || "Invalid or expired OTP.";
      toast.error(msg);
    },
  });

  // ── Step 3: reset password ──────────────────────────────────────────────────
  const resetPasswordMutation = useMutation({
    mutationFn: async () => {
      const res = await apiClient.post(`/forgot-password/reset`, {
        reset_token: resetToken,
        password,
        password_confirmation: passwordConfirm,
      });
      return res.data;
    },
    onSuccess: () => {
      toast.success("Password reset successfully! Please sign in.");
      setTimeout(() => navigate(route.signin), 1200);
    },
    onError: (err) => {
      const msg =
        err.response?.data?.message ||
        err.message ||
        "Failed to reset password.";
      toast.error(msg);
    },
  });

  // ── Derived flags ───────────────────────────────────────────────────────────
  const passwordsMatch =
    password && passwordConfirm && password === passwordConfirm;
  const passwordTooShort = password.length > 0 && password.length < 8;

  // ── Titles per step ─────────────────────────────────────────────────────────
  const stepMeta = {
    1: {
      icon: KeyRound,
      title: "Forgot Password",
      description:
        "Enter your username and we'll send an OTP to your account email.",
    },
    2: {
      icon: MailCheck,
      title: "Verify OTP",
      description: `Enter the 6-digit code sent to the email linked to "${username}".`,
    },
    3: {
      icon: ShieldCheck,
      title: "Set New Password",
      description: "Choose a strong password - at least 8 characters.",
    },
  };

  const meta = stepMeta[step];
  const StepIcon = meta.icon;

  return (
    <div className="relative min-h-[100dvh] flex items-center justify-center p-4 md:p-6 lg:p-8 overflow-hidden bg-slate-50/30 dark:bg-transparent">
      {/* Atmospheric blobs */}
      <div className="absolute inset-0 -z-10">
        <div className="absolute top-[-5%] left-[-5%] w-[600px] h-[600px] bg-primary/5 rounded-full blur-[120px] animate-pulse" />
        <div className="absolute bottom-[-5%] right-[-5%] w-[600px] h-[600px] bg-primary/10 rounded-full blur-[120px] animate-pulse" />
      </div>

      <div className="w-full max-w-[420px] space-y-6 lg:space-y-8 animate-in fade-in slide-in-from-bottom-8 duration-1000">
        {/* Brand */}
        <div className="flex flex-col items-center text-center space-y-3">
          <div className="group relative">
            <div className="absolute -inset-1 rounded-2xl bg-gradient-to-tr from-primary/30 to-primary/10 opacity-40 blur-md transition duration-500 group-hover:opacity-70" />
            <div className="relative flex h-16 w-16 items-center justify-center rounded-2xl overflow-hidden bg-white dark:bg-card border border-border shadow-xs group-hover:scale-105 transition-transform shrink-0 p-1.5">
              <img
                src="/assets/img/logo-small.png"
                alt="Easy Retail Flow"
                className="h-full w-full object-contain"
              />
            </div>
          </div>
          <div className="flex flex-col items-center space-y-1">
            <h1 className="text-4xl sm:text-5xl font-black tracking-tight text-slate-900 dark:text-white drop-shadow-xs">
              Easy Retail Flow
            </h1>
            <span className="text-xs font-semibold text-primary uppercase tracking-wider">
              Multi-Store POS
            </span>
          </div>
        </div>

        {/* Card */}
        <Card className="border-none shadow-[0_20px_50px_rgba(0,0,0,0.1)] bg-card/70 backdrop-blur-xl ring-1 ring-border/50">
          <CardHeader className="space-y-1 pt-8 pb-4 text-center border-b border-border/40">
            <div className="flex justify-center mb-3">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
                <StepIcon className="h-6 w-6 text-primary" />
              </div>
            </div>
            <CardTitle className="text-xl font-bold tracking-tight">
              {meta.title}
            </CardTitle>
            <CardDescription className="text-muted-foreground/80 font-medium px-4 text-sm">
              {meta.description}
            </CardDescription>
          </CardHeader>

          <CardContent className="pt-6 px-8">
            <StepIndicator current={step} />

            {/* ── Step 1 ── */}
            {step === 1 && (
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  if (username.trim()) sendOtpMutation.mutate();
                }}
                className="grid gap-5"
              >
                <div className="grid gap-2.5">
                  <Label
                    htmlFor="username"
                    className="text-xs font-semibold uppercase tracking-wider text-muted-foreground/90 ml-1"
                  >
                    Username
                  </Label>
                  <div className="relative">
                    <User className="absolute left-3.5 top-3 h-4 w-4 text-muted-foreground/70" />
                    <Input
                      id="username"
                      type="text"
                      placeholder="Enter your username"
                      className="pl-11 h-12 bg-background/50 border-border/80 focus:border-primary/50 focus:ring-primary/20 transition-all rounded-xl"
                      value={username}
                      onChange={(e) => setUsername(e.target.value)}
                      required
                      autoFocus
                    />
                  </div>
                </div>

                <Button
                  className="w-full h-12 text-sm font-bold tracking-wide shadow-xl shadow-primary/20 hover:scale-[1.01] transition-all rounded-xl"
                  type="submit"
                  disabled={sendOtpMutation.isPending || !username.trim()}
                >
                  {sendOtpMutation.isPending ? (
                    <div className="flex items-center gap-2">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      <span>Sending OTP...</span>
                    </div>
                  ) : (
                    "Send OTP"
                  )}
                </Button>
              </form>
            )}

            {/* ── Step 2 ── */}
            {step === 2 && (
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  if (otpDigits.length === 6) verifyOtpMutation.mutate();
                }}
                className="grid gap-6"
              >
                <div className="space-y-3">
                  <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground/90 ml-1 block text-center">
                    Enter 6-digit OTP
                  </Label>
                  <OtpInput value={otpValue} onChange={setOtp} />
                  <p className="text-[11px] text-center text-muted-foreground">
                    Didn&apos;t receive the code?{" "}
                    <button
                      type="button"
                      className="text-primary font-semibold hover:underline"
                      onClick={() => {
                        setOtp("      ");
                        setStep(1);
                      }}
                    >
                      Try again
                    </button>
                  </p>
                </div>

                <Button
                  className="w-full h-12 text-sm font-bold tracking-wide shadow-xl shadow-primary/20 hover:scale-[1.01] transition-all rounded-xl"
                  type="submit"
                  disabled={verifyOtpMutation.isPending || otpDigits.length < 6}
                >
                  {verifyOtpMutation.isPending ? (
                    <div className="flex items-center gap-2">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      <span>Verifying...</span>
                    </div>
                  ) : (
                    "Verify OTP"
                  )}
                </Button>
              </form>
            )}

            {/* ── Step 3 ── */}
            {step === 3 && (
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  if (passwordsMatch && !passwordTooShort) {
                    resetPasswordMutation.mutate();
                  }
                }}
                className="grid gap-5"
              >
                <div className="grid gap-2.5">
                  <Label
                    htmlFor="new-password"
                    className="text-xs font-semibold uppercase tracking-wider text-muted-foreground/90 ml-1"
                  >
                    New Password
                  </Label>
                  <div className="relative">
                    <Lock className="absolute left-3.5 top-3 h-4 w-4 text-muted-foreground/70" />
                    <Input
                      id="new-password"
                      type={showPassword ? "text" : "password"}
                      placeholder="••••••••"
                      className={cn(
                        "pl-11 pr-11 h-12 bg-background/50 border-border/80 focus:border-primary/50 focus:ring-primary/20 transition-all rounded-xl",
                        passwordTooShort &&
                        "border-destructive focus:border-destructive",
                      )}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      autoFocus
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3.5 top-3 text-muted-foreground/50 hover:text-primary transition-colors p-1 rounded-md"
                    >
                      {showPassword ? (
                        <EyeOff className="h-4 w-4" />
                      ) : (
                        <Eye className="h-4 w-4" />
                      )}
                    </button>
                  </div>
                  {passwordTooShort && (
                    <p className="text-[11px] text-destructive ml-1">
                      Password must be at least 8 characters.
                    </p>
                  )}
                </div>

                <div className="grid gap-2.5">
                  <Label
                    htmlFor="confirm-password"
                    className="text-xs font-semibold uppercase tracking-wider text-muted-foreground/90 ml-1"
                  >
                    Confirm Password
                  </Label>
                  <div className="relative">
                    <Lock className="absolute left-3.5 top-3 h-4 w-4 text-muted-foreground/70" />
                    <Input
                      id="confirm-password"
                      type={showConfirm ? "text" : "password"}
                      placeholder="••••••••"
                      className={cn(
                        "pl-11 pr-11 h-12 bg-background/50 border-border/80 focus:border-primary/50 focus:ring-primary/20 transition-all rounded-xl",
                        passwordConfirm &&
                        !passwordsMatch &&
                        "border-destructive focus:border-destructive",
                        passwordsMatch &&
                        "border-emerald-500 focus:border-emerald-500",
                      )}
                      value={passwordConfirm}
                      onChange={(e) => setPasswordConfirm(e.target.value)}
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirm(!showConfirm)}
                      className="absolute right-3.5 top-3 text-muted-foreground/50 hover:text-primary transition-colors p-1 rounded-md"
                    >
                      {showConfirm ? (
                        <EyeOff className="h-4 w-4" />
                      ) : (
                        <Eye className="h-4 w-4" />
                      )}
                    </button>
                  </div>
                  {passwordConfirm && !passwordsMatch && (
                    <p className="text-[11px] text-destructive ml-1">
                      Passwords do not match.
                    </p>
                  )}
                  {passwordsMatch && (
                    <p className="text-[11px] text-emerald-600 ml-1">
                      Passwords match ✓
                    </p>
                  )}
                </div>

                <Button
                  className="w-full h-12 text-sm font-bold tracking-wide shadow-xl shadow-primary/20 hover:scale-[1.01] transition-all rounded-xl"
                  type="submit"
                  disabled={
                    resetPasswordMutation.isPending ||
                    !passwordsMatch ||
                    passwordTooShort
                  }
                >
                  {resetPasswordMutation.isPending ? (
                    <div className="flex items-center gap-2">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      <span>Resetting Password...</span>
                    </div>
                  ) : (
                    "Reset Password"
                  )}
                </Button>
              </form>
            )}
          </CardContent>

          <CardFooter className="flex flex-col pt-2 pb-8 px-8">
            <div className="w-full border-t border-border/40 pt-5 flex flex-col items-center gap-3">
              <Link
                to={route.signin}
                className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground hover:text-primary transition-colors"
              >
                <ArrowLeft className="h-3.5 w-3.5" />
                Back to Sign In
              </Link>
              <p className="text-[10px] text-center text-slate-500 dark:text-slate-400 font-bold uppercase tracking-[0.2em] leading-relaxed">
                © {new Date().getFullYear()} Easy Retail Flow • Multi-Store POS
              </p>
            </div>
          </CardFooter>
        </Card>
      </div>
    </div>
  );
};

export default ForgotPassword;
